import { useRef, useState } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import dayjs from "dayjs";

import { InboundLayout, InboundHeaderCard } from "ui-components/Layout";
import {
  InboundSubmitButton,
  InboundDetailAddButton,
  InboundDetailEditButton,
} from "ui-components/Form";

import { headerRender, headerValues, headerValidation } from "./po-receiving-schema";
import { detailRender, detailValues, detailValidation } from "./po-receiving-details-schema";
import { useCustomerValue } from "providers/CustomerContext";
import { useVendorOptions } from "hooks/useVendorQuery";
import { Success, Error_, InfoDialog, LoadingDialog } from "ui-components/Alert";
import { skuValidation, dateValidation } from "layouts/views/common/validation";
import { useMutation, useQuery } from "@tanstack/react-query";
import POReceiving from "./components/POReceiving";
import POReceivingDetail from "./components/POReceivingDetail";
import { poRecvColumns, poRecvViewColumns } from "./columns";
import { getASNOptions, getOpenASN, createPOReceiving } from "services/data.inbound.service";
import { statusLabel } from "layouts/views/common/enum";
import BasicTable2 from "ui-components/tables/BasicTable2";
import {
  deleteDetail,
  getSplitDetailsTotalQuantity,
  getTotalQuantity,
  updateDetail,
  serialValidation_,
} from "../common/common";
import { PORecvCaption } from "../components/Caption";
import ApplyAll from "../components/ApplyAll";
import { useNavigate } from "react-router-dom";
import { useVendorOptionsWithName } from "hooks/useQueryHook";
import BasicViewTable from "ui-components/tables/BasicViewTable";
import POReceivingDivisionGroup from "./components/POReceivingDivisionGroup";
import { getShowDivision } from "services/data.inbound.service";

const defaultValues = {
  ...headerValues,
  dateReceived: dayjs(new Date()).format("YYYY-MM-DD"),
  recvType: "Standard Receipt (Normal)",
  status: "Open",
};
function AddPOReceiving() {
  const navigate = useNavigate();
  const { customerCode } = useCustomerValue();
  const [vendorOptions, setVendorOptions] = useState([]);
  const formValues = { ...defaultValues, customerCode };
  const [alert, setAlert] = useState(null);
  const [details, setDetails] = useState([]);
  const [notScannedDetails, setNotScannedDetails] = useState([]);
  const [asnOptions, setASNOptions] = useState([]);
  const onError = (data) => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    setAlert(<Error_ data={data} onChange={setAlert} />);
  };
  const createPOReceivingMutation = useMutation(createPOReceiving, {});
  const createStatus = createPOReceivingMutation.status === "loading";
  const [uploadData, setUploadData] = useState([]);
  const [showDivision, setShowDivision] = useState(false);
  useVendorOptionsWithName({
    customerCode,
    onSuccess: (data) => {
      const vendors = data.data.data;

      if (vendors.length > 0) {
        setValue("vendorCode", vendors[0].vendorCode);
        setValue("sellerCompany", vendors[0].vendShortName);
      }
      setVendorOptions(vendors.map((x) => x.vendorCode));
    },
  });

  const form = useForm({
    defaultValues: formValues,
    values: formValues,
    resolver: yupResolver(headerValidation),
  });
  const {
    control,
    watch,
    formState: { errors },
    setValue,
    getValues,
    trigger,
  } = form;

  const headerProps = {
    headerRender,
    customerCode,
    asnOptions,
    vendorOptions,
    control,
    setValue,
    errors,
  };

  const [asnCode, vendorCode] = watch(["asnCode", "vendorCode"]);

  useQuery({
    queryKey: ["asn-options", { customerCode, vendorCode }],
    queryFn: () => getASNOptions({ customerCode, vendorCode }),
    enabled: customerCode !== null && vendorCode !== null,
    onSuccess: (data) => setASNOptions(data.data.data),
    onError,
  });

  useQuery({
    queryKey: ["show-division", customerCode],
    queryFn: () => getShowDivision({ customerCode }),
    enabled: Boolean(customerCode),
    onSuccess: (data) => setShowDivision(data.data.data),
    onError,
  });

  const { isFetching } = useQuery({
    queryKey: ["asn", { asnCode }],
    queryFn: () => getOpenASN({ asnNumber: asnCode }),
    enabled: asnCode !== null,
    onSuccess: (data) => {
      const {
        dateExpected,
        containerNo,
        containerType,
        truckNo,
        sellerCompany,
        sellerAddress1,
        sellerAddress2,
        sellerPostcode,
        sellerCity,
        sellerState,
        sellerCountry,
        sellerPhoneNo,
        sellerFaxNo,
        remark,
        asnDetails,
        notScannedASNDetails,
      } = data.data.data;
      setValue("arrivalDate", dateExpected)
      setValue("containerNo", containerNo);
      setValue("containerType", containerType);
      setValue("truckNo", truckNo);
      setValue("sellerCompany", sellerCompany);
      setValue("sellerAddress1", sellerAddress1);
      setValue("sellerAddress2", sellerAddress2);
      setValue("sellerPostcode", sellerPostcode);
      setValue("sellerCity", sellerCity);
      setValue("sellerState", sellerState);
      setValue("sellerCountry", sellerCountry);
      setValue("sellerPhoneNo", sellerPhoneNo);
      setValue("sellerFaxNo", sellerFaxNo);
      setValue("remark", remark);
      setDetails(
        asnDetails.map((x) => {
          if (x.inbSerialNbrCtrl) {
            const emptyItems =
              Boolean(x.serialNumbers) ?
                Array.from({ length: x.quantity - x.serialNumbers?.length ?? 0 }, () => ({ serialNumber: "", newItem: true, active: true })) :
                Array.from({ length: x.quantity }, () => ({ serialNumber: "", newItem: true, active: true }));
            const resultSerial = Boolean(x.serialNumbers) ?
              [...x.serialNumbers.map(y => { return { serialNumber: y, newItem: true, active: true } }), ...emptyItems] :
              [...emptyItems];

            return {
              ...x,
              status: statusLabel[x.status],
              uom: { label: x.baseUOM, value: x.baseQuantity },
              serialNumbers: resultSerial
            }
          } else {
            return {
              ...x,
              status: statusLabel[x.status],
              uom: { label: x.baseUOM, value: x.baseQuantity },
              serialNumbers: []
            }
          }


        })
      );
      setNotScannedDetails(
        notScannedASNDetails.map((x) => {
          const emptyItems = Array.from({ length: x.quantity }, () => ({ serialNumber: "", newItem: true, active: true }));
          return {
            ...x,
            serialNumbers: emptyItems
          }
        })
      );
    },
    onError,
  });
  const detailProps = {
    details,
    setDetails,
    notScannedDetails,
    values: { customerCode, vendorCode },
     uploadData,
    setUploadData,
    showDivision,
  };

  const onSubmit = async () => {
    const isValid = await trigger();
    if (!isValid) {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      return;
    }

    if (details.length === 0) {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      setAlert(<Error_ content="Invalid PO Receiving Details" onChange={setAlert} />);
      return;
    }


    const serialNumbersMap = new Map();
    const affectedDets = new Set();

    for (const det of details) {
      for (const serial of det.serialNumbers) {
        const { serialNumber } = serial;
        if (serialNumbersMap.has(serialNumber)) {
          affectedDets.add(det.lineNo);
          affectedDets.add(serialNumbersMap.get(serialNumber).lineNo);
        } else {
          serialNumbersMap.set(serialNumber, det);
        }
      }
    }
    const result = Array.from(affectedDets);
    if (result.length > 0) {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      setAlert(
        <Error_
          content={`Duplicate Serial Numbers in LineNo ${result.join(", ")}`}
          onChange={setAlert}
        />
      );
      return;
    }

    const notTally = details.filter(x => x.inbSerialNbrCtrl && x.quantity !== x.serialNumbers.length);
    if (notTally.length > 0) {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      setAlert(
        <Error_
          content={`Serial Numbers Count Not Same as Quantity in LineNo ${notTally.map(x => { return x.lineNo }).join(", ")}`}
          onChange={setAlert}
        />
      );
      return;
    }

    const data = getValues();

    const req = {
      ...data,divisionQuantity: JSON.stringify(uploadData),
      poRecvDetails: details.map((d) => ({ ...d, status: "O", uom: d.uom.label })),
    };

    createPOReceivingMutation.mutate(req, {
      onSuccess: (data) => navigate("/inbound/po-receiving", { state: data.data.message }),
      onError,
    });
  };
  return (
    <InboundLayout lg={12}>
      {isFetching && <LoadingDialog />}
      {alert}
      <InboundHeaderCard>
        <form noValidate>
          <POReceiving {...headerProps} />
        </form>
      </InboundHeaderCard>

      <Detail {...detailProps} />
      <InboundSubmitButton
        label={createStatus ? "Submit..." : "Submit"}
        onClick={onSubmit}
        disabled={details.length === 0 || createStatus}
      />
    </InboundLayout>
  );
}

const Detail = ({ details, setDetails, notScannedDetails, values, uploadData, setUploadData, showDivision }) => {
  const columns = [
    {
      field: "actions",
      headerName: "Action",
      renderActions: ["Add", "Delete"],
      type: "actions",
    },
    ...poRecvColumns,
  ];
  const [alert, setAlert] = useState(null);
  const [serialAlert, setSerialAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [formValues, setFormValues] = useState(null);
  const doRef = useRef(null);
  const invRef = useRef(null);
  const lineNo = formValues ? formValues.lineNo : details.length + 1;
  console.log(showDivision,"show division in detail");

  const form = useForm({
    defaultValues: { ...detailValues, lineNo, inbSerialNbrCtrl: false, serialNumbers: [] },
    values: formValues || {
      ...detailValues,
      lineNo,
      status: "",
      vDONo: doRef.current,
      vInvNo: invRef.current,
      inbSerialNbrCtrl: false,
      serialNumbers: []
    },
    resolver: yupResolver(detailValidation),
  });
  const {
    control,
    watch,
    formState: { errors, isDirty, dirtyFields },
    setValue,
    getValues,
    handleSubmit,
  } = form;
  const [skuCode, recvWh, recvZone] = watch(["skuCode", "recvWh", "recvZone"]);

  const onSubmit = (data) => {
    setSerialAlert(null);

    if (Boolean(dirtyFields.serialNumbers)) {
      if (data.inbSerialNbrCtrl && data.quantity !== data.serialNumbers.length) {
        const errorMsg =
          data.quantity > data.serialNumbers.length
            ? `Not Enough Serial Number(${data.serialNumbers.length})`
            : `Too Many Serial Number(${data.serialNumbers.length})`;
        setSerialAlert(<Error_ content={errorMsg} onChange={setSerialAlert} />);
        return;
      }
      const isSerialAnyDuplicate = serialValidation_(details, data);
      if (isSerialAnyDuplicate.isValid) {
        if (isSerialAnyDuplicate.type === "Single") {
          const errorMsg = "Duplicate Serial Number " + isSerialAnyDuplicate.serialNumbers.join(", ");
          setSerialAlert(<Error_ content={errorMsg} onChange={setSerialAlert} />);
          return;
        } else if (isSerialAnyDuplicate.type === "Multiple") {
          const errorMsg =
            "Duplicate Serial Number " +
            isSerialAnyDuplicate.serialNumbers.join(", ") +
            " In LineNo " +
            isSerialAnyDuplicate.lineNos.join(", ");
          setSerialAlert(<Error_ content={errorMsg} onChange={setSerialAlert} />);
          return;
        }
      }
    }

    doRef.current = data.vDONo;
    invRef.current = data.vInvNo;
    setDetails(updateDetail(details, data));
    setFormValues(null);
    setAlert(<Success content="Updated" onChange={setAlert} />);
  };
  const onRowClick = (values) => {
    setSerialAlert(null);
    if (formValues !== null && isDirty) {
      const data = getValues();

      if (Boolean(dirtyFields.serialNumbers)) {
        //check current serial number, if invalid then keep focus on
        if (data.inbSerialNbrCtrl && data.quantity !== data.serialNumbers.length) {
          const errorMsg =
            data.quantity > data.serialNumbers.length
              ? `Not Enough Serial Number(${data.serialNumbers.length})`
              : `Too Many Serial Number(${data.serialNumbers.length})`;
          setSerialAlert(<Error_ content={errorMsg} onChange={setSerialAlert} />);
          return false;
        }
        const isSerialAnyDuplicate = serialValidation_(details, data);
        if (isSerialAnyDuplicate.isValid) {
          if (isSerialAnyDuplicate.type === "Single") {
            const errorMsg =
              "Duplicate Serial Number " + isSerialAnyDuplicate.serialNumbers.join(", ");
            setSerialAlert(<Error_ content={errorMsg} onChange={setSerialAlert} />);
            return false;
          } else if (isSerialAnyDuplicate.type === "Multiple") {
            const errorMsg =
              "Duplicate Serial Number " +
              isSerialAnyDuplicate.serialNumbers.join(", ") +
              " In LineNo " +
              isSerialAnyDuplicate.lineNos.join(", ");
            setSerialAlert(<Error_ content={errorMsg} onChange={setSerialAlert} />);
            return false;
          }
        }
      }

      setDialog(
        <InfoDialog
          title={`Unsaved changes for Line No ${formValues.lineNo}`}
          content="Do you want to save the changes"
          onOk={() => {
            const data = getValues();
            setDetails(updateDetail(details, data));
            setDialog(null);
            setFormValues(values);
          }}
          onClose={() => {
            setDialog(null);
            setFormValues(values);
          }}
        />
      );
    } else {
      setFormValues(values);
    }
    return true;
  };
  const onAdd = (data) => {
    setDetails([...details, { ...data, lineNo: details.length + 1 }]);
    setAlert(<Success content="Added" onChange={setAlert} />);
  };
  const onDelete = (data) => {
    setDetails(deleteDetail(details, data));
    setFormValues(null);
    setAlert(<Success content="Deleted" onChange={setAlert} />);
  };

  const detailProps = {
    detailRender,
    values: { ...values, skuCode, recvWh, recvZone },
    control,
    errors,
    setValue,
    getValues,
    serialAlert
  };
  const totalQuantity = getTotalQuantity(details, "quantity");
  const totalExpectedQuantity = getSplitDetailsTotalQuantity(details, "qtyExpected", "poLineNo");
  const caption = (
    <PORecvCaption
      totalRows={details.length}
      totalQuantity={totalQuantity}
      totalExpectedQuantity={totalExpectedQuantity}
    />
  );
  const applyAllProps = { formValues, setFormValues, setValue, details, setDetails };
  const tableProps = { columns, rows: details, caption, onRowClick, onAdd, onDelete };
  return (
    <InboundHeaderCard>
      {dialog}
      {alert}
      <ApplyAll {...applyAllProps} />

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <POReceivingDetail {...detailProps} />
        {formValues !== null && (
          <InboundDetailEditButton disabled={!isDirty} onClick={() => setFormValues(null)} />
        )}
      </form>

      <BasicTable2 {...tableProps} />
      {notScannedDetails.length > 0 && (
        <div style={{ marginTop: "1rem" }}>
          <BasicViewTable
            title="Datagrid Display (ASN details not scanned)"
            rows={notScannedDetails}
            columns={poRecvViewColumns}
          />
        </div>
      )}
      {showDivision && (
      <POReceivingDivisionGroup
  //       details={[]}
  // uploadData={[]}
  // setUploadData={() => {}}
          details={details}
           uploadData={uploadData}
           setUploadData={setUploadData}
        />)}
    </InboundHeaderCard>
    
  );
};

export default AddPOReceiving;
